import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  Tabs,
  Tab,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  notificationCredentialsAPI,
  userSettingsAPI,
} from "../../services/api";

/**
 * SMS rows from GET may nest fields under `credentials` or expose AWS fields on the row.
 * @param {Record<string, unknown> | null | undefined} smsRow
 * @returns {{ aws_access_key_id: string; aws_secret_access_key: string; aws_region: string; sender_id: string } | null}
 */
function normalizeSmsRow(smsRow) {
  if (!smsRow || typeof smsRow !== "object") return null;
  const nested = smsRow.credentials;
  const c =
    nested != null && typeof nested === "object" && !Array.isArray(nested)
      ? nested
      : smsRow;
  return {
    aws_access_key_id:
      c.aws_access_key_id != null ? String(c.aws_access_key_id) : "",
    aws_secret_access_key:
      c.aws_secret_access_key != null ? String(c.aws_secret_access_key) : "",
    aws_region:
      c.aws_region != null ? String(c.aws_region) : "ap-south-1",
    sender_id: c.sender_id != null ? String(c.sender_id) : "",
  };
}

export function AlertRulesSettings({ open, onClose }) {
  // Settings state
  const [commonSettings, setCommonSettings] = useState({
    min: "",
    max: "",  
  });

  const [telegramSettings, setTelegramSettings] = useState({
    service_type: "TELEGRAM",
    bot_token: "",
  });

  const [emailSettings, setEmailSettings] = useState({
    service_type: "EMAIL",
    server: "",
    port: 587,
    username: "",
    password: "",
    sender: "",
    encryption: "tls",
  });

  const [smsSettings, setSmsSettings] = useState({
    service_type: "SMS",
    aws_access_key_id: "",
    aws_secret_access_key: "",
    aws_region: "ap-south-1",
    sender_id: "",
  });

  const [loading, setLoading] = useState(false);
  /** Which settings already exist on the server (per tab create vs update). */
  const [serverSegment, setServerSegment] = useState({
    cooldownRange: false,
    telegram: false,
    email: false,
    sms: false,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /** False until the first credentials/cooldown fetch for this dialog open finishes (avoids overwriting user edits). */
  const [settingsReady, setSettingsReady] = useState(false);
  /** 0: Cooldown Interval, 1: Email, 2: SMS, 3: Telegram */
  const [settingsTab, setSettingsTab] = useState(0);
  const openRef = useRef(open);
  openRef.current = open;

  const credentialStateRef = useRef({
    telegramSettings,
    emailSettings,
    smsSettings,
    commonSettings,
  });
  credentialStateRef.current = {
    telegramSettings,
    emailSettings,
    smsSettings,
    commonSettings,
  };

  const formFieldsDisabled = !settingsReady;

  // Fetch settings on open
  useEffect(() => {
    if (!open) {
      setSettingsReady(false);
      return;
    }
    setSettingsTab(0);
    fetchSettings({ resetFormLock: true });
  }, [open]);

  /**
   * @param {{ resetFormLock?: boolean }} [options]
   * - resetFormLock: when true (dialog just opened), disable fields until load completes so a slow response cannot overwrite in-progress edits.
   */
  const fetchSettings = async ({ resetFormLock = false } = {}) => {
    try {
      setLoading(true);
      if (resetFormLock) {
        setSettingsReady(false);
      }

      // Set default common settings
      setCommonSettings({ min: "", max: "" });

      let hasCooldownOnServer = false;
      let hasTelegramOnServer = false;
      let hasEmailOnServer = false;
      let hasSmsOnServer = false;

      // Fetch both APIs in parallel for better performance
      const [cooldownResponse, allCredentialsResponse] =
        await Promise.allSettled([
          userSettingsAPI.getCooldownRange(),
          notificationCredentialsAPI.getAll(),
        ]);

      // Process cooldown range response
      if (cooldownResponse.status === "fulfilled") {
        const cooldownResult = cooldownResponse.value;
        console.log("Cooldown range fetched:", cooldownResult);

        // Extract cooldown range from response
        // API response structure: { success: true, data: { min, max } } or { min, max }
        const cooldownData = cooldownResult?.data || cooldownResult;
        if (
          cooldownData &&
          (cooldownData.min !== undefined || cooldownData.max !== undefined)
        ) {
          setCommonSettings({
            min:
              cooldownData.min !== undefined && cooldownData.min !== null
                ? Number(cooldownData.min)
                : "",
            max:
              cooldownData.max !== undefined && cooldownData.max !== null
                ? Number(cooldownData.max)
                : "",
          });
          hasCooldownOnServer = true;
        }
      } else {
        console.log(
          "No cooldown range found or error:",
          cooldownResponse.reason
        );
        // Keep defaults if fetch fails
      }

      // Process notification credentials response
      if (allCredentialsResponse.status === "fulfilled") {
        const allCredentialsResult = allCredentialsResponse.value;
        console.log(
          "All notification credentials fetched:",
          allCredentialsResult
        );

        // Extract services array from API response
        // API response structure: { success: true, message: "...", data: { services: [...] } }
        let servicesArray = [];
        if (
          allCredentialsResult?.data?.services &&
          Array.isArray(allCredentialsResult.data.services)
        ) {
          servicesArray = allCredentialsResult.data.services;
        } else if (Array.isArray(allCredentialsResult)) {
          servicesArray = allCredentialsResult;
        } else if (
          allCredentialsResult?.services &&
          Array.isArray(allCredentialsResult.services)
        ) {
          servicesArray = allCredentialsResult.services;
        }

        console.log("Extracted services array:", servicesArray);

        // Parse services array
        if (servicesArray.length > 0) {
          // Array format: [{ service_type: "TELEGRAM", credentials: {...} }, ...]
          const telegramCreds = servicesArray.find(
            (cred) => cred.service_type === "TELEGRAM"
          );
          if (
            telegramCreds &&
            telegramCreds.credentials &&
            telegramCreds.credentials.bot_token
          ) {
            setTelegramSettings({
              service_type: "TELEGRAM",
              bot_token: telegramCreds.credentials.bot_token,
            });
            hasTelegramOnServer = true;
          } else {
            setTelegramSettings({ service_type: "TELEGRAM", bot_token: "" });
          }

          const emailCreds = servicesArray.find(
            (cred) => cred.service_type === "EMAIL"
          );
          if (emailCreds && emailCreds.credentials) {
            setEmailSettings({
              service_type: "EMAIL",
              server: emailCreds.credentials.server || "",
              port: emailCreds.credentials.port || 587,
              username: emailCreds.credentials.username || "",
              password: emailCreds.credentials.password || "",
              sender: emailCreds.credentials.sender || "",
              encryption: emailCreds.credentials.encryption || "tls",
            });
            if (emailCreds.credentials.server) {
              hasEmailOnServer = true;
            }
          } else {
            setEmailSettings({
              service_type: "EMAIL",
              server: "",
              port: 587,
              username: "",
              password: "",
              sender: "",
              encryption: "tls",
            });
          }

          const smsCreds = servicesArray.find(
            (cred) => cred.service_type === "SMS"
          );
          const smsFromArray = normalizeSmsRow(smsCreds);
          if (smsFromArray) {
            setSmsSettings({
              service_type: "SMS",
              ...smsFromArray,
            });
            if (smsFromArray.aws_access_key_id) {
              hasSmsOnServer = true;
            }
          } else {
            setSmsSettings({
              service_type: "SMS",
              aws_access_key_id: "",
              aws_secret_access_key: "",
              aws_region: "ap-south-1",
              sender_id: "",
            });
          }
        } else if (
          allCredentialsResult &&
          typeof allCredentialsResult === "object"
        ) {
          // Object format: { TELEGRAM: { credentials: {...} }, EMAIL: {...}, SMS: {...} }
          if (
            allCredentialsResult.TELEGRAM &&
            allCredentialsResult.TELEGRAM.credentials
          ) {
            setTelegramSettings({
              service_type: "TELEGRAM",
              bot_token:
                allCredentialsResult.TELEGRAM.credentials.bot_token || "",
            });
            if (allCredentialsResult.TELEGRAM.credentials.bot_token) {
              hasTelegramOnServer = true;
            }
          } else {
            setTelegramSettings({ service_type: "TELEGRAM", bot_token: "" });
          }

          if (
            allCredentialsResult.EMAIL &&
            allCredentialsResult.EMAIL.credentials
          ) {
            setEmailSettings({
              service_type: "EMAIL",
              server: allCredentialsResult.EMAIL.credentials.server || "",
              port: allCredentialsResult.EMAIL.credentials.port || 587,
              username: allCredentialsResult.EMAIL.credentials.username || "",
              password: allCredentialsResult.EMAIL.credentials.password || "",
              sender: allCredentialsResult.EMAIL.credentials.sender || "",
              encryption:
                allCredentialsResult.EMAIL.credentials.encryption || "tls",
            });
            if (allCredentialsResult.EMAIL.credentials.server) {
              hasEmailOnServer = true;
            }
          } else {
            setEmailSettings({
              service_type: "EMAIL",
              server: "",
              port: 587,
              username: "",
              password: "",
              sender: "",
              encryption: "tls",
            });
          }

          const smsFromObject = normalizeSmsRow(allCredentialsResult.SMS);
          if (smsFromObject) {
            setSmsSettings({
              service_type: "SMS",
              ...smsFromObject,
            });
            if (smsFromObject.aws_access_key_id) {
              hasSmsOnServer = true;
            }
          } else {
            setSmsSettings({
              service_type: "SMS",
              aws_access_key_id: "",
              aws_secret_access_key: "",
              aws_region: "ap-south-1",
              sender_id: "",
            });
          }
        } else {
          // No credentials found, set defaults
          setTelegramSettings({ service_type: "TELEGRAM", bot_token: "" });
          setEmailSettings({
            service_type: "EMAIL",
            server: "",
            port: 587,
            username: "",
            password: "",
            sender: "",
            encryption: "tls",
          });
          setSmsSettings({
            service_type: "SMS",
            aws_access_key_id: "",
            aws_secret_access_key: "",
            aws_region: "ap-south-1",
            sender_id: "",
          });
        }
      } else {
        console.log(
          "No notification credentials found or error:",
          allCredentialsResponse.reason
        );
        // Set defaults if fetch fails
        setTelegramSettings({ service_type: "TELEGRAM", bot_token: "" });
        setEmailSettings({
          service_type: "EMAIL",
          server: "",
          port: 587,
          username: "",
          password: "",
          sender: "",
          encryption: "tls",
        });
        setSmsSettings({
          service_type: "SMS",
          aws_access_key_id: "",
          aws_secret_access_key: "",
          aws_region: "ap-south-1",
          sender_id: "",
        });
      }

      setServerSegment({
        cooldownRange: hasCooldownOnServer,
        telegram: hasTelegramOnServer,
        email: hasEmailOnServer,
        sms: hasSmsOnServer,
      });
    } catch (error) {
      console.error("Error fetching settings:", error);
      // If 404 or no data, that's okay - user can add data
      if (error.status !== 404) {
        setSnackbar({
          open: true,
          message: "Failed to load settings",
          severity: "error",
        });
      } else {
        setServerSegment({
          cooldownRange: false,
          telegram: false,
          email: false,
          sms: false,
        });
      }
    } finally {
      setLoading(false);
      if (openRef.current) {
        setSettingsReady(true);
      }
    }
  };

  const handleSave = async () => {
    const {
      telegramSettings: tg,
      emailSettings: em,
      smsSettings: sms,
      commonSettings: common,
    } = credentialStateRef.current;

    const tab = settingsTab;
    const seg = serverSegment;

    const nonEmpty = (v) => String(v ?? "").trim() !== "";

    const tabLabels = [
      "Cooldown interval",
      "Email",
      "SMS",
      "Telegram",
    ];
    const verb = (saved) => (saved ? "updated" : "saved");

    /** Parsed cooldown for tab 0 after validation (set only when tab === 0). */
    let parsedCooldownRange = null;

    if (tab === 0) {
      const minV =
        common.min === "" ? NaN : Number(common.min);
      const maxV =
        common.max === "" ? NaN : Number(common.max);
      if (!Number.isFinite(minV) || !Number.isFinite(maxV)) {
        setSnackbar({
          open: true,
          message:
            "Please enter valid numbers for minimum and maximum cooldown (seconds).",
          severity: "error",
        });
        return;
      }
      if (minV > maxV) {
        setSnackbar({
          open: true,
          message: "Minimum cooldown cannot be greater than maximum.",
          severity: "error",
        });
        return;
      }
      parsedCooldownRange = { min: minV, max: maxV };
    } else if (tab === 1) {
      if (
        !nonEmpty(em.server) ||
        !nonEmpty(em.username) ||
        !nonEmpty(em.sender)
      ) {
        setSnackbar({
          open: true,
          message:
            "Please enter SMTP server, username, and sender address before saving.",
          severity: "error",
        });
        return;
      }
      if (!seg.email && !nonEmpty(em.password)) {
        setSnackbar({
          open: true,
          message:
            "Please enter the email account password when creating a new configuration.",
          severity: "error",
        });
        return;
      }
    } else if (tab === 2) {
      if (
        !nonEmpty(sms.aws_access_key_id) ||
        !nonEmpty(sms.aws_secret_access_key) ||
        !nonEmpty(sms.sender_id)
      ) {
        setSnackbar({
          open: true,
          message:
            "Please enter AWS access key, secret key, and sender ID before saving SMS settings. Use Delete to remove saved credentials.",
          severity: "error",
        });
        return;
      }
    } else if (tab === 3) {
      if (!nonEmpty(tg.bot_token)) {
        setSnackbar({
          open: true,
          message:
            "Please enter the Telegram bot token before saving. Use Delete to remove saved credentials.",
          severity: "error",
        });
        return;
      }
    }

    try {
      setLoading(true);

      if (tab === 0) {
        try {
          await userSettingsAPI.updateCooldownRange(parsedCooldownRange);
        } catch (cooldownError) {
          console.error("Error saving cooldown range:", cooldownError);
          throw new Error("Failed to save cooldown range");
        }
        setSnackbar({
          open: true,
          message: `${tabLabels[0]} ${verb(seg.cooldownRange)} successfully!`,
          severity: "success",
        });
      } else if (tab === 1) {
        const portNum = Number(em.port);
        const emailCredentials = {
          server: em.server || "",
          port: Number.isFinite(portNum) ? portNum : 587,
          username: em.username || "",
          password: em.password || "",
          sender: em.sender || "",
          encryption: em.encryption || "tls",
        };
        try {
          await notificationCredentialsAPI.update(
            "EMAIL",
            emailCredentials
          );
        } catch (emailError) {
          console.error("Error saving EMAIL credentials:", emailError);
          throw new Error("Failed to save EMAIL credentials");
        }
        setSnackbar({
          open: true,
          message: `${tabLabels[1]} ${verb(seg.email)} successfully!`,
          severity: "success",
        });
      } else if (tab === 2) {
        const smsCredentials = {
          aws_access_key_id: sms.aws_access_key_id || "",
          aws_secret_access_key: sms.aws_secret_access_key || "",
          aws_region: sms.aws_region || "ap-south-1",
          sender_id: sms.sender_id || "",
        };
        try {
          await notificationCredentialsAPI.update(
            "SMS",
            smsCredentials
          );
        } catch (smsError) {
          console.error("Error saving SMS credentials:", smsError);
          throw new Error("Failed to save SMS credentials");
        }
        setSnackbar({
          open: true,
          message: `${tabLabels[2]} ${verb(seg.sms)} successfully!`,
          severity: "success",
        });
      } else if (tab === 3) {
        const telegramCredentials = {
          bot_token: tg.bot_token || "",
        };
        try {
          await notificationCredentialsAPI.update(
            "TELEGRAM",
            telegramCredentials
          );
        } catch (telegramError) {
          console.error("Error saving TELEGRAM credentials:", telegramError);
          throw new Error("Failed to save TELEGRAM credentials");
        }
        setSnackbar({
          open: true,
          message: `${tabLabels[3]} ${verb(seg.telegram)} successfully!`,
          severity: "success",
        });
      }

      await fetchSettings({ resetFormLock: false });

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error("Error saving settings:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to save settings",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteService = (serviceType) => {
    setServiceToDelete(serviceType);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteService = async () => {
    if (!serviceToDelete) return;

    try {
      setLoading(true);

      // Delete TELEGRAM, EMAIL, or SMS using notification-credentials API
      if (serviceToDelete === "TELEGRAM") {
        await notificationCredentialsAPI.delete("TELEGRAM");
        setTelegramSettings({ service_type: "TELEGRAM", bot_token: "" });
      } else if (serviceToDelete === "EMAIL") {
        await notificationCredentialsAPI.delete("EMAIL");
        setEmailSettings({
          service_type: "EMAIL",
          server: "",
          port: 587,
          username: "",
          password: "",
          sender: "",
          encryption: "tls",
        });
      } else if (serviceToDelete === "SMS") {
        await notificationCredentialsAPI.delete("SMS");
        setSmsSettings({
          service_type: "SMS",
          aws_access_key_id: "",
          aws_secret_access_key: "",
          aws_region: "ap-south-1",
          sender_id: "",
        });
      }

      setSnackbar({
        open: true,
        message: `${serviceToDelete} settings deleted successfully!`,
        severity: "success",
      });

      // Refresh settings
      await fetchSettings({ resetFormLock: false });
    } catch (error) {
      console.error("Error deleting settings:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to delete settings",
        severity: "error",
      });
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setServiceToDelete(null);
    }
  };

  const handleCancel = () => {
    fetchSettings({ resetFormLock: false }); // Reload settings to discard changes
    onClose();
  };

  const currentTabSavedOnServer =
    settingsTab === 0
      ? serverSegment.cooldownRange
      : settingsTab === 1
        ? serverSegment.email
        : settingsTab === 2
          ? serverSegment.sms
          : serverSegment.telegram;

  return (
    <>
      <Dialog
        open={open}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SettingsIcon sx={{ color: "rgb(255, 133, 85)" }} />
            <Typography
              sx={{
                lineHeight: "1.6",
                letterSpacing: "0.0075em",
                flex: "0 0 auto",
                fontFamily: "Roboto, sans-serif",
                fontSize: "20px",
                color: "rgb(255, 133, 85)",
                fontWeight: 600,
              }}
            >
              Alert Rules Settings
            </Typography>
          </Box>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {!settingsReady ? (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Roboto, sans-serif",
                color: "#4A5565",
                mb: 2,
              }}
            >
              Loading settings…
            </Typography>
          ) : (
            <>
          <Tabs
            value={settingsTab}
            onChange={(_, next) => setSettingsTab(next)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 2,
              mt: 1,
              "& .MuiTab-root": {
                fontFamily: "Roboto, sans-serif",
                textTransform: "none",
                fontWeight: 500,
              },
              "& .Mui-selected": {
                color: "rgb(255, 133, 85)",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "rgb(255, 133, 85)",
              },
            }}
          >
            <Tab
              label="Cooldown Interval"
              id="alert-rules-settings-tab-0"
              aria-controls="alert-rules-settings-panel-0"
            />
            <Tab
              label="Email"
              id="alert-rules-settings-tab-1"
              aria-controls="alert-rules-settings-panel-1"
            />
            <Tab
              label="SMS"
              id="alert-rules-settings-tab-2"
              aria-controls="alert-rules-settings-panel-2"
            />
            <Tab
              label="Telegram"
              id="alert-rules-settings-tab-3"
              aria-controls="alert-rules-settings-panel-3"
            />
          </Tabs>

          {/* Cooldown Interval */}
          <Box
            role="tabpanel"
            hidden={settingsTab !== 0}
            id="alert-rules-settings-panel-0"
            aria-labelledby="alert-rules-settings-tab-0"
            sx={{ display: settingsTab === 0 ? "block" : "none", pt: 1 }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "16px",
                fontWeight: 600,
                color: "#212121",
                mb: 2,
              }}
            >
              Cooldown Interval
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                label="Minimum"
                type="number"
                value={commonSettings.min === "" ? "" : commonSettings.min}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "") {
                    setCommonSettings((prev) => ({ ...prev, min: "" }));
                    return;
                  }
                  const n = parseInt(v, 10);
                  if (!Number.isNaN(n)) {
                    setCommonSettings((prev) => ({ ...prev, min: n }));
                  }
                }}
                fullWidth
                disabled={formFieldsDisabled}
                inputProps={{ min: 1, max: 100 }}
                sx={{
                  "& .MuiInputLabel-root": {
                    fontFamily: "Roboto, sans-serif",
                  },
                  "& .MuiOutlinedInput-input": {
                    fontFamily: "Roboto, sans-serif",
                  },
                }}
              />
              <TextField
                label="Maximum"
                type="number"
                value={commonSettings.max === "" ? "" : commonSettings.max}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "") {
                    setCommonSettings((prev) => ({ ...prev, max: "" }));
                    return;
                  }
                  const n = parseInt(v, 10);
                  if (!Number.isNaN(n)) {
                    setCommonSettings((prev) => ({ ...prev, max: n }));
                  }
                }}
                fullWidth
                disabled={formFieldsDisabled}
                inputProps={{ min: 1, max: 100 }}
                sx={{
                  "& .MuiInputLabel-root": {
                    fontFamily: "Roboto, sans-serif",
                  },
                  "& .MuiOutlinedInput-input": {
                    fontFamily: "Roboto, sans-serif",
                  },
                }}
              />
            </Box>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "12px",
                color: "#4A5565",
                fontStyle: "italic",
              }}
            >
              These values define the minimum and maximum cooldown interval (in
              seconds) for alert rules.
            </Typography>
          </Box>

          {/* Email */}
          <Box
            role="tabpanel"
            hidden={settingsTab !== 1}
            id="alert-rules-settings-panel-1"
            aria-labelledby="alert-rules-settings-tab-1"
            sx={{ display: settingsTab === 1 ? "block" : "none", pt: 1 }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "16px",
                fontWeight: 600,
                color: "#212121",
                mb: 2,
              }}
            >
              EMAIL Service Configuration
            </Typography>
            <TextField
              label="Server"
              value={emailSettings.server}
              onChange={(e) =>
                setEmailSettings((prev) => ({
                  ...prev,
                  server: e.target.value,
                }))
              }
              fullWidth
              required
              disabled={formFieldsDisabled}
              sx={{
                mb: 2,
                "& .MuiInputLabel-root": {
                  fontFamily: "Roboto, sans-serif",
                },
                "& .MuiOutlinedInput-input": {
                  fontFamily: "Roboto, sans-serif",
                },
              }}
            />
            <TextField
              label="Port"
              type="number"
              value={emailSettings.port}
              onChange={(e) =>
                setEmailSettings((prev) => ({
                  ...prev,
                  port: parseInt(e.target.value, 10) || 587,
                }))
              }
              fullWidth
              disabled={formFieldsDisabled}
              sx={{
                mb: 2,
                "& .MuiInputLabel-root": {
                  fontFamily: "Roboto, sans-serif",
                },
                "& .MuiOutlinedInput-input": {
                  fontFamily: "Roboto, sans-serif",
                },
              }}
            />
            <TextField
              label="Username"
              value={emailSettings.username}
              onChange={(e) =>
                setEmailSettings((prev) => ({
                  ...prev,
                  username: e.target.value,
                }))
              }
              fullWidth
              required
              disabled={formFieldsDisabled}
              sx={{
                mb: 2,
                "& .MuiInputLabel-root": {
                  fontFamily: "Roboto, sans-serif",
                },
                "& .MuiOutlinedInput-input": {
                  fontFamily: "Roboto, sans-serif",
                },
              }}
            />
            <TextField
              label="Password"
              type="password"
              value={emailSettings.password}
              onChange={(e) =>
                setEmailSettings((prev) => ({
                  ...prev,
                  password: e.target.value,
                }))
              }
              fullWidth
              required
              disabled={formFieldsDisabled}
              sx={{
                mb: 2,
                "& .MuiInputLabel-root": {
                  fontFamily: "Roboto, sans-serif",
                },
                "& .MuiOutlinedInput-input": {
                  fontFamily: "Roboto, sans-serif",
                },
              }}
            />
            <TextField
              label="Sender"
              value={emailSettings.sender}
              onChange={(e) =>
                setEmailSettings((prev) => ({
                  ...prev,
                  sender: e.target.value,
                }))
              }
              fullWidth
              required
              disabled={formFieldsDisabled}
              sx={{
                mb: 2,
                "& .MuiInputLabel-root": {
                  fontFamily: "Roboto, sans-serif",
                },
                "& .MuiOutlinedInput-input": {
                  fontFamily: "Roboto, sans-serif",
                },
              }}
            />
            <FormControl
              fullWidth
              sx={{ mb: 2 }}
              disabled={formFieldsDisabled}
            >
              <InputLabel sx={{ fontFamily: "Roboto, sans-serif" }}>
                Encryption
              </InputLabel>
              <Select
                value={emailSettings.encryption}
                onChange={(e) =>
                  setEmailSettings((prev) => ({
                    ...prev,
                    encryption: e.target.value,
                  }))
                }
                label="Encryption"
                sx={{ fontFamily: "Roboto, sans-serif" }}
              >
                <MenuItem value="tls" sx={{ fontFamily: "Roboto, sans-serif" }}>
                  TLS
                </MenuItem>
                <MenuItem value="ssl" sx={{ fontFamily: "Roboto, sans-serif" }}>
                  SSL
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* SMS */}
          <Box
            role="tabpanel"
            hidden={settingsTab !== 2}
            id="alert-rules-settings-panel-2"
            aria-labelledby="alert-rules-settings-tab-2"
            sx={{ display: settingsTab === 2 ? "block" : "none", pt: 1 }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "16px",
                fontWeight: 600,
                color: "#212121",
                mb: 2,
              }}
            >
              SMS Service Configuration
            </Typography>
            <TextField
              label="AWS Access Key ID"
              value={smsSettings.aws_access_key_id}
              onChange={(e) =>
                setSmsSettings((prev) => ({
                  ...prev,
                  aws_access_key_id: e.target.value,
                }))
              }
              fullWidth
              required
              disabled={formFieldsDisabled}
              sx={{
                mb: 2,
                "& .MuiInputLabel-root": {
                  fontFamily: "Roboto, sans-serif",
                },
                "& .MuiOutlinedInput-input": {
                  fontFamily: "Roboto, sans-serif",
                },
              }}
            />
            <TextField
              label="AWS Secret Access Key"
              type="password"
              value={smsSettings.aws_secret_access_key}
              onChange={(e) =>
                setSmsSettings((prev) => ({
                  ...prev,
                  aws_secret_access_key: e.target.value,
                }))
              }
              fullWidth
              required
              disabled={formFieldsDisabled}
              sx={{
                mb: 2,
                "& .MuiInputLabel-root": {
                  fontFamily: "Roboto, sans-serif",
                },
                "& .MuiOutlinedInput-input": {
                  fontFamily: "Roboto, sans-serif",
                },
              }}
            />
            <TextField
              label="AWS Region"
              value={smsSettings.aws_region}
              onChange={(e) =>
                setSmsSettings((prev) => ({
                  ...prev,
                  aws_region: e.target.value,
                }))
              }
              fullWidth
              disabled={formFieldsDisabled}
              sx={{
                mb: 2,
                "& .MuiInputLabel-root": {
                  fontFamily: "Roboto, sans-serif",
                },
                "& .MuiOutlinedInput-input": {
                  fontFamily: "Roboto, sans-serif",
                },
              }}
            />
            <TextField
              label="Sender ID"
              value={smsSettings.sender_id}
              onChange={(e) =>
                setSmsSettings((prev) => ({
                  ...prev,
                  sender_id: e.target.value,
                }))
              }
              fullWidth
              disabled={formFieldsDisabled}
              sx={{
                "& .MuiInputLabel-root": {
                  fontFamily: "Roboto, sans-serif",
                },
                "& .MuiOutlinedInput-input": {
                  fontFamily: "Roboto, sans-serif",
                },
              }}
            />
          </Box>

          {/* Telegram */}
          <Box
            role="tabpanel"
            hidden={settingsTab !== 3}
            id="alert-rules-settings-panel-3"
            aria-labelledby="alert-rules-settings-tab-3"
            sx={{ display: settingsTab === 3 ? "block" : "none", pt: 1 }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "16px",
                fontWeight: 600,
                color: "#212121",
                mb: 2,
              }}
            >
              TELEGRAM Service Configuration
            </Typography>
            <TextField
              label="Bot Token"
              value={telegramSettings.bot_token}
              onChange={(e) =>
                setTelegramSettings((prev) => ({
                  ...prev,
                  bot_token: e.target.value,
                }))
              }
              fullWidth
              required
              disabled={formFieldsDisabled}
              sx={{
                "& .MuiInputLabel-root": {
                  fontFamily: "Roboto, sans-serif",
                },
                "& .MuiOutlinedInput-input": {
                  fontFamily: "Roboto, sans-serif",
                },
              }}
            />
          </Box>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button
            onClick={handleCancel}
            sx={{
              fontFamily: "Roboto, sans-serif",
              textTransform: "none",
              color: "#4A5565",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading || !settingsReady}
            sx={{
              fontFamily: "Roboto, sans-serif",
              textTransform: "none",
              backgroundColor: "rgb(255, 133, 85)",
              "&:hover": {
                backgroundColor: "#1f28c7",
              },
            }}
          >
            {loading
              ? currentTabSavedOnServer
                ? "Updating..."
                : "Creating..."
              : currentTabSavedOnServer
              ? "Update"
              : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        PaperProps={{
          sx: {
            backgroundColor: "rgb(255, 255, 255)",
            color: "rgba(0, 0, 0, 0.87)",
            boxShadow:
              "rgba(0, 0, 0, 0.2) 0px 11px 15px -7px, rgba(0, 0, 0, 0.14) 0px 24px 38px 3px, rgba(0, 0, 0, 0.12) 0px 9px 46px 8px",
            position: "relative",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            maxHeight: "calc(100% - 64px)",
            maxWidth: "600px",
            width: "calc(100% - 64px)",
            transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)",
            borderRadius: "4px",
            margin: "32px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Roboto, sans-serif",
            margin: "0px",
            color: "#D32F2F",
            fontWeight: 500,
            fontSize: "1.25rem",
            lineHeight: "1.6",
            letterSpacing: "0.0075em",
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent
          sx={{
            fontFamily: "Roboto, sans-serif",
            margin: "0px 0px 16px",
            fontWeight: 400,
            fontSize: "1rem",
            lineHeight: "1.5",
            letterSpacing: "0.00938em",
            color: "rgba(0, 0, 0, 0.6)",
          }}
        >
          Are you sure you want to delete {serviceToDelete} service
          configuration? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setServiceToDelete(null);
            }}
            sx={{ fontFamily: "Roboto, sans-serif" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteService}
            color="error"
            variant="contained"
            disabled={loading}
            sx={{ fontFamily: "Roboto, sans-serif" }}
          >
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ fontFamily: "Roboto, sans-serif" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
